import { Loading, Modal, TextInput } from 'carbon-components-react';
import { useReducerSpread } from '../../utils/common.util';
import CustomButton from '../../components/custom-button/custom-btn.component';
import { useEffect } from 'react';
import { postMethod, putMethod } from '../../api/common';
import { useCustomMutation } from '../../utils/useCustomMutation';
import useSWR, { mutate } from 'swr';
import { ConfirmModal } from '../../components/modal/confirm-modal/ConfirmModal.component';

const initialData = {
  name: '',
  amount: '',
  classAmounts: [],
};

function FeeTypeForm({ isOpen, closeModal, mutation, data }) {
  const { data: classList, isLoading } = useSWR(
    isOpen && !data ? 'class/list' : null,
  );

  const [formData, setFormData] = useReducerSpread(initialData);

  useEffect(() => {
    if (!isOpen) return; // Only run when modal is open
    if (data) {
      setFormData({
        ...initialData,
        classAmounts: classList?.map((cls) => ({
          classId: cls?.id,
          classDescription: cls?.description,
          amount: '',
        })),
        ...data,
      });
    } else if (classList) {
      setFormData({
        ...initialData,
        classAmounts: classList.map((cls) => ({
          classId: cls?.id,
          classDescription: cls?.description,
          amount: '',
        })),
      });
    }
  }, [isOpen, data, classList, setFormData]);

  function handleSubmit(e) {
    e.preventDefault();

    const filteredClassAmounts = formData.classAmounts.filter(
      (cls) => cls.amount !== '' && Number(cls.amount) > 0,
    );
    if (filteredClassAmounts.length === 0) {
      return alert('Please enter valid amounts for at least one class.');
    }

    const payload = {
      ...formData,
      classAmounts: filteredClassAmounts,
    };
    mutation.trigger(payload, {
      onSuccess() {
        handleClose();
        mutate('daily-billing/fees/list');
      },
    });
  }

  function handleClose() {
    setFormData(initialData);
    closeModal();
  }

  return (
    <Modal
      id="create-fee-modal"
      className="create-fee-modal"
      hasScrollingContent
      passiveModal={true}
      open={isOpen}
      modalHeading={`${data ? 'Edit' : 'Create'} Fee Type`}
      primaryButtonDisabled={false}
    >
      {' '}
      <form action="post">
        <p className="text-14 mt-10">
          {data
            ? `Edit ${data?.name} to update the daily fees categorization.`
            : 'Create a fee type to categorize the daily fees collected.'}
        </p>
        <div className="mt-20">
          <TextInput
            id="name"
            name="name"
            labelText="Fee Name"
            placeholder="Enter fee name"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
          />
        </div>
        <div className="mt-20">
          <TextInput
            id="amount"
            name="amount"
            type="number"
            labelText="Fee Amount"
            placeholder="Enter fee amount"
            tabIndex={0}
            value={formData.amount}
            onChange={(e) => {
              const newAmount = e.target.value;
              setFormData({
                amount: newAmount,
                classAmounts: formData.classAmounts.map((cls) => ({
                  ...cls,
                  amount: newAmount,
                })),
              });
            }}
          />
        </div>

        <div className="mt-20">
          <div>
            <p className="text-14">Fee per Class</p>
          </div>
          {isLoading ? (
            <div className="d-flex-center">
              <Loading withOverlay={false} small={true} />
            </div>
          ) : (
            <div
              className="mt-10"
              style={{
                display: 'grid',
                gap: '20px',
                gridTemplateColumns: 'repeat(4, 1fr)',
              }}
            >
              {formData?.classAmounts?.map((cls, index) => (
                <div
                  key={cls.classId}
                  style={{
                    display: 'flex',
                    gap: '5px',
                    alignItems: 'center',
                  }}
                >
                  <label
                    htmlFor={`classId-${cls.classId}`}
                    className="text-14 mt-5"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {cls.classDescription}
                  </label>
                  <TextInput
                    id={`classId-${cls.classId}`}
                    name={`classId-${cls.classId}`}
                    type="number"
                    className="text-input"
                    size="sm"
                    placeholder=""
                    labelText=""
                    value={cls.amount}
                    onChange={(e) =>
                      setFormData({
                        classAmounts: formData.classAmounts.map((fee, i) =>
                          i === index
                            ? {
                                ...fee,
                                amount: e.target.value,
                              }
                            : fee,
                        ),
                      })
                    }
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        {mutation?.error && (
          <p className="text-14 mt-10 text-danger">
            {mutation?.error?.message}
          </p>
        )}

        <div
          style={{
            marginTop: '80px',
            display: 'flex',
            gap: '10px',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <CustomButton
            classes="outline danger no-border no-hover font-bold"
            style={{ flex: '1' }}
            disabled={mutation?.isMutating}
            onClick={handleClose}
          >
            CLOSE
          </CustomButton>

          <CustomButton
            type="submit"
            style={{ flex: '1' }}
            classes={`primary font-bold`}
            onClick={handleSubmit}
            disabled={
              mutation?.isMutating ||
              isLoading ||
              !formData.name ||
              !formData.amount
              // formData.classAmounts.some(
              //   (fee) => fee.amount === '' || Number(fee.amount) <= 0,
              // )
            }
          >
            {mutation?.isMutating ? (
              <div className="d-flex-center">
                <Loading withOverlay={false} small={true} />
              </div>
            ) : (
              'Save'
            )}
          </CustomButton>
        </div>
      </form>
    </Modal>
  );
}

export function CreateFeeType({ isOpen, closeModal }) {
  const create = useCreateFeeType();

  return (
    <FeeTypeForm isOpen={isOpen} closeModal={closeModal} mutation={create} />
  );
}

function useCreateFeeType() {
  return useCustomMutation(
    'daily-billing/fees/create',
    (url, { arg }) => postMethod(url, arg),
    {
      invalidateKey: 'daily-billing/fees/list',
    },
  );
}
export function EditFeeType({ isOpen, closeModal, data }) {
  const edit = useEditFeeType(data?.id);

  return (
    <FeeTypeForm
      isOpen={isOpen}
      closeModal={closeModal}
      mutation={edit}
      data={data}
    />
  );
}

function useEditFeeType(id) {
  return useCustomMutation(`daily-billing/fees/${id}/update`, (url, { arg }) =>
    putMethod(url, arg),
  );
}

const groupInitialData = {
  name: '',
  amount: null,
};
export function GroupFeeTypeForm({ isOpen, closeModal, mutation, data }) {
  const [formData, setFormData] = useReducerSpread(groupInitialData);

  useEffect(() => {
    if (!isOpen) return; // Only run when modal is open
    if (data) {
      setFormData({
        ...data,
      });
    } else {
      setFormData(groupInitialData);
    }
  }, [isOpen, data, setFormData]);

  function handleClose() {
    setFormData(groupInitialData);
    mutation?.reset();
    closeModal();
  }

  return (
    <ConfirmModal
      isOpen={isOpen}
      closeModal={closeModal}
      title={`${data ? 'Edit' : 'Create'} Group Based Fee Type`}
      description={
        <>
          <TextInput
            id="name"
            name="name"
            className="mb-20"
            labelText="Fee Name"
            placeholder="Enter fee name"
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
          />

          <TextInput
            id={`group-fee-amount`}
            name={`group-fee-amount`}
            type="number"
            labelText="Amount"
            placeholder="Enter amount"
            value={formData.amount}
            onChange={(e) =>
              setFormData({ amount: parseFloat(e.target.value) })
            }
          />
        </>
      }
      actionText={data ? 'UPDATE' : 'CREATE'}
      onClick={() =>
        mutation?.trigger(formData, {
          onSuccess() {
            handleClose();
            mutate('daily-billing/fees/list');
          },
        })
      }
      disabled={mutation?.isMutating || !formData.name || !formData.amount}
      isLoading={mutation?.isMutating}
      errorMessage={mutation?.error ? mutation?.error.message : null}
      actionBtnClasses="primary"
    />
  );
}

export function CreateGroupFeeType({ isOpen, closeModal }) {
  const create = useCustomMutation(
    'daily-billing/fees/create',
    (url, { arg }) => postMethod(url, arg),
    {
      invalidateKey: 'daily-billing/fees/list',
    },
  );

  return (
    <GroupFeeTypeForm
      isOpen={isOpen}
      closeModal={closeModal}
      mutation={create}
    />
  );
}

export function EditGroupFeeType({ isOpen, closeModal, data }) {
  const edit = useCustomMutation(
    `daily-billing/fees/${data?.id}/update`,
    (url, { arg }) => putMethod(url, arg),
  );

  return (
    <GroupFeeTypeForm
      isOpen={isOpen}
      closeModal={closeModal}
      mutation={edit}
      data={data}
    />
  );
}
